import { memo } from "react"
import { FiEye, FiTrash2 } from "react-icons/fi"

import type { ClassSessionResponse } from "@/types/class_session"
import {
  Button,
  Flex,
  Heading,
  IconButton,
  Separator,
  Stack,
  Text,
} from "@chakra-ui/react"

const formatFieldValue = (value: unknown, type: string): string => {
  if (value === null || value === undefined || value === "") {
    return "N/A"
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "N/A"
  }

  if (type === "bool") {
    if (typeof value === "string") {
      if (value.toLowerCase() === "true") return "Yes"
      if (value.toLowerCase() === "false") return "No"
      return value
    }
    return value ? "Yes" : "No"
  }

  if (type === "datetime" || type === "date" || type === "time") {
    const dateValue = new Date(String(value))
    if (!Number.isNaN(dateValue.getTime())) {
      if (type === "date") return dateValue.toLocaleDateString()
      if (type === "time") return dateValue.toLocaleTimeString()
      return dateValue.toLocaleString()
    }
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value)
    } catch (error) {
      return "N/A"
    }
  }

  return String(value)
}

interface ClassSessionCardProps {
  classSession: ClassSessionResponse
  onEdit?: (classSession: ClassSessionResponse) => void
  onDelete?: (classSession: ClassSessionResponse) => void
  onView?: (classSession: ClassSessionResponse) => void
}

const ClassSessionCard = memo(
  ({ classSession, onEdit, onDelete, onView }: ClassSessionCardProps) => {
    const titleValue = classSession.class_template_id ?? classSession.id

    const subtitleValue = classSession.instructor_id ?? undefined

    return (
      <Stack
        borderWidth="1px"
        borderRadius="lg"
        padding={4}
        gap={4}
        bg="bg.surface"
        shadow="sm"
      >
        <Stack gap={1}>
          <Heading size="sm">{titleValue}</Heading>
          {subtitleValue !== undefined && (
            <Text color="fg.muted" fontSize="sm">
              {subtitleValue || "No description"}
            </Text>
          )}
        </Stack>

        <Stack gap={1} fontSize="xs" color="fg.subtle">
          <Text>
            Created: {new Date(classSession.created_at).toLocaleString()}
          </Text>
          <Text>
            Updated: {new Date(classSession.updated_at).toLocaleString()}
          </Text>
        </Stack>

        <Separator />
        <Stack gap={2} fontSize="sm">
          <Flex
            key="class_template_id"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Class Template Id</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(classSession.class_template_id, "str")}
            </Text>
          </Flex>
          <Flex
            key="instructor_id"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Instructor Id</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(classSession.instructor_id, "str")}
            </Text>
          </Flex>
          <Flex
            key="session_date"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Session Date</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(classSession.session_date, "date")}
            </Text>
          </Flex>
          <Flex
            key="session_start"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Session Start</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(classSession.session_start, "datetime")}
            </Text>
          </Flex>
          <Flex
            key="session_end"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Session End</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(classSession.session_end, "datetime")}
            </Text>
          </Flex>
          <Flex key="status" justify="space-between" gap={4} align="flex-start">
            <Text fontWeight="medium">Status</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(classSession.status, "str")}
            </Text>
          </Flex>
          <Flex
            key="cancellation_reason"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Cancellation Reason</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(classSession.cancellation_reason, "str")}
            </Text>
          </Flex>
          <Flex
            key="location_override"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Location Override</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(classSession.location_override, "str")}
            </Text>
          </Flex>
          <Flex
            key="max_students_override"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Max Students Override</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(classSession.max_students_override, "int")}
            </Text>
          </Flex>
          <Flex key="notes" justify="space-between" gap={4} align="flex-start">
            <Text fontWeight="medium">Notes</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(classSession.notes, "str")}
            </Text>
          </Flex>
        </Stack>

        <Flex gap={2} justify="flex-end">
          {onView && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onView(classSession)}
            >
              <FiEye /> View
            </Button>
          )}
          {onEdit && (
            <Button
              size="sm"
              variant="outline"
              colorScheme="blue"
              onClick={() => onEdit(classSession)}
            >
              Edit
            </Button>
          )}
          {onDelete && (
            <IconButton
              size="sm"
              variant="ghost"
              colorScheme="red"
              aria-label="Delete classSession"
              onClick={() => onDelete(classSession)}
            >
              <FiTrash2 />
            </IconButton>
          )}
        </Flex>
      </Stack>
    )
  },
)

ClassSessionCard.displayName = "ClassSessionCard"

export default ClassSessionCard
